/* c8 ignore start */
import { DEV_MODE } from '../../global/env';
import { canOrientScreen, IS_CLIENT } from '../../utils/support';
import { DisposalBin, listen, vdsEvent } from '../events';
import { Logger } from '../logger';
/**
 * Contains the logic for managing the window's screen orientation.
 *
 * @example
 * ```ts
 * import { LitElement } from 'lit';
 * import { ScreenOrientationController } from '@vidstack/elements';
 *
 * class MyElement extends LitElement {
 *   screenOrientationController = new ScreenOrientationController(this);
 * }
 * ```
 */
export class ScreenOrientationController {
    constructor(_host) {
        this._host = _host;
        this._isScreenOrientationLocked = false;
        this._updateScreenOrientation();
        if (DEV_MODE) {
            this._logger = new Logger(_host, { owner: this });
        }
        this._listenerDisposal = new DisposalBin(_host, DEV_MODE && { name: 'listenerDisposal', owner: this });
        _host.addController({
            hostConnected: this._handleHostConnected.bind(this),
            hostDisconnected: this._handleHostDisconnected.bind(this)
        });
    }
    async _handleHostConnected() {
        this._updateScreenOrientation();
        this._addScreenOrientationEventListeners();
    }
    /**
     * Dispose of any event listeners and unlock screen orientation (if locked).
     */
    async _handleHostDisconnected() {
        if (this.canOrient && this._isScreenOrientationLocked)
            await this.unlock();
        this._listenerDisposal.empty();
    }
    /**
     * The current screen orientation. It will return `undefined` if the Screen Orientation API
     * is not available.
     */
    get currentOrientation() {
        return this._screenOrientation;
    }
    /**
     * Whether the native Screen Orientation API is available.
     */
    get canOrient() {
        return canOrientScreen();
    }
    /**
     * Whether the screen orientation is currently locked.
     *
     * @default false
     */
    get isLocked() {
        return this._isScreenOrientationLocked;
    }
    /**
     * Locks the orientation of the player to the desired orientation type using the
     * Screen Orientation API. This method will throw an error if the API is unavailable.
     *
     * @param lockType - The screen lock orientation type.
     * @link https://developer.mozilla.org/en-US/docs/Web/API/Screen/orientation
     * @see https://w3c.github.io/screen-orientation
     */
    async lock(lockType) {
        this._throwIfScreenOrientationUnavailable();
        if (DEV_MODE) {
            this._logger.info('locking screen orientation', lockType);
        }
        await screen.orientation.lock(lockType);
        this._isScreenOrientationLocked = true;
        this._host.dispatchEvent(vdsEvent('vds-screen-orientation-lock-change', {
            bubbles: true,
            composed: true,
            detail: lockType
        }));
    }
    /**
     * Unlocks the orientation of the player to it's default state using the Screen Orientation
     * API. This method will throw an error if the API is unavailable.
     *
     * @link https://developer.mozilla.org/en-US/docs/Web/API/Screen/orientation
     * @see https://w3c.github.io/screen-orientation
     */
    async unlock() {
        this._throwIfScreenOrientationUnavailable();
        if (DEV_MODE) {
            this._logger.info('unlocking screen orientation');
        }
        await screen.orientation.unlock();
        this._isScreenOrientationLocked = false;
        this._host.dispatchEvent(vdsEvent('vds-screen-orientation-lock-change', {
            bubbles: true,
            composed: true,
            detail: screen.orientation.type
        }));
    }
    _addScreenOrientationEventListeners() {
        if (!this.canOrient)
            return;
        this._listenerDisposal.add(this._addScreenOrientationChangeEventListener());
    }
    /**
     * @returns Stop listening function.
     */
    _addScreenOrientationChangeEventListener() {
        return listen(screen.orientation, 'change', this._handleOrientationChange.bind(this));
    }
    _handleOrientationChange(event) {
        this._screenOrientation = window.screen.orientation
            .type;
        if (DEV_MODE && this._isScreenOrientationLocked) {
            this._logger.info('screen orientation changed', this._screenOrientation);
        }
        this._host.dispatchEvent(vdsEvent('vds-screen-orientation-change', {
            bubbles: true,
            composed: true,
            detail: this._screenOrientation,
            originalEvent: event
        }));
    }
    _updateScreenOrientation() {
        var _a, _b;
        this._screenOrientation = IS_CLIENT
            ? (_b = (_a = window.screen) === null || _a === void 0 ? void 0 : _a.orientation) === null || _b === void 0 ? void 0 : _b.type
            : undefined;
    }
    /**
     * @throws {Error} - Will throw if Screen Orientation API is unavailable.
     */
    _throwIfScreenOrientationUnavailable() {
        if (this.canOrient)
            return;
        throw Error('Screen orientation API is not available.');
    }
}
/* c8 ignore stop*/
